import { Coordinate, RainConfig, TileSize } from "./models"
import type { TileMapMetaProvider } from "./tile_map_meta_provider"
import { BODY_GROUND_OFFSET } from "./lpc/body_placement"

/** Coverage at a ground point, independent of roof cutaways and trimmed interior boundaries. */
export const isRainCoveredAt = (provider: TileMapMetaProvider, at: Coordinate): boolean => {
    const x = Math.floor(at.x / TileSize), y = Math.floor(at.y / TileSize)
    const roof = provider.resolveTileMapFromLayerId?.("sky")?.[x]?.[y]
    return (!!roof && roof !== "0") || !!provider.isSkyTileAt?.(x, y) ||
        provider.isUnderRoofAt({ x: at.x, y: at.y - BODY_GROUND_OFFSET })
}

const smooth = (v: number) => { const t = Math.max(0, Math.min(1, v)); return t * t * (3 - 2 * t) }

/** Rain duration includes its gentle arrival and departure. Clouds arrive first and leave last. */
export const rainEnvelope = (config: RainConfig, elapsedMs: number): { rain: number; cloud: number } => {
    if (config.clearing) return {
        rain: config.clearing.rain * smooth(1 - elapsedMs / 5000),
        cloud: config.clearing.cloud * smooth(1 - elapsedMs / 10000),
    }
    const age = elapsedMs - config.cloudLeadMs
    const ramp = Math.min(5000, config.durationMs / 2)
    const rain = smooth(age / ramp) * smooth((config.durationMs - age) / ramp)
    // Chapter two starts its introductory rain immediately (cloudLeadMs=0). At the first
    // client frame elapsedMs is also zero: dividing them poisons storm wind with NaN, which
    // then reaches the tree sprites' transforms. No lead means clouds arrive as a step.
    const arrival = config.cloudLeadMs > 0 ? smooth(elapsedMs / config.cloudLeadMs) : elapsedMs >= 0 ? 1 : 0
    const cloud = arrival * smooth((config.durationMs + 5000 - age) / 5000)
    return { rain, cloud }
}
